import React from "react";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import {
  InvoiceDraftDocumentData,
  printInvoicePreviewDocument,
  UnsavedInvoicePreviewModal,
} from "../../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import type { BillingDetails, MemberLookupOption } from "./eventSalesShared";

export type EventInvoiceDraftPreviewLineItem = Readonly<{
  code?: string | null;
  description: string;
  discountPercentage?: number | null;
  id?: string;
  quantity: number;
  unitPriceInCents: number;
  totalInCents: number;
  note?: string | null;
}>;

type EventInvoiceDraftPreviewProps = Readonly<{
  billing: BillingDetails;
  chamber?: React.ComponentProps<typeof UnsavedInvoicePreviewModal>["chamber"];
  creating?: boolean;
  dueDate: string;
  eventCode?: string | null;
  eventName: string;
  eventStartAtUtcMilli?: number | null;
  eventTimeZone?: string | null;
  invoiceDate: string;
  invoiceKind: InvoiceDraftDocumentData["invoiceKind"];
  lineItems: readonly EventInvoiceDraftPreviewLineItem[];
  manualPaymentMethods?: InvoiceDraftDocumentData["manualPaymentMethods"];
  /** Create lookups: Sold To prints the selected member's company details. */
  members?: readonly MemberLookupOption[];
  onCreate: () => Promise<void> | void;
  onClose: () => void;
  /** Design (15) black "Save Draft" (Monday D30); hidden when not passed. */
  onSaveDraft?: () => Promise<void> | void;
  open: boolean;
  /** Invoice note, for example the tax exempt customer note. */
  publicMemo?: string | null;
  referencePurchaseOrder?: string;
  subtotalInCents: number;
  taxInCents: number;
  taxLabel: string;
  /** Design (15) note under the tax line (sponsorship invoices). */
  taxNote?: string | null;
  taxRegistrationNumber?: string | null;
  totalInCents: number;
}>;

const EventInvoiceDraftPreview = ({
  billing,
  chamber,
  creating,
  dueDate,
  eventCode,
  eventName,
  eventStartAtUtcMilli,
  eventTimeZone,
  invoiceDate,
  invoiceKind,
  lineItems,
  manualPaymentMethods,
  members,
  onCreate,
  onClose,
  onSaveDraft,
  open,
  publicMemo,
  referencePurchaseOrder,
  subtotalInCents,
  taxInCents,
  taxLabel,
  taxNote,
  taxRegistrationNumber,
  totalInCents,
}: EventInvoiceDraftPreviewProps): React.ReactElement => {
  const billingSnapshot = {
    addressLine1: billing.streetAddress,
    addressLine2: billing.addressLine2,
    city: billing.city,
    contactEmail: billing.billingEmail,
    contactName: billing.billingContactName,
    contactPhone: billing.billingPhone,
    country: billing.country,
    customerName: billing.customerName,
    postalCode: billing.postalCode,
    province: billing.province,
  };
  const normalizedEventTimeZone = normalizeCmpTimeZone(eventTimeZone).timeZone;
  const memberCompanySoldTo = billing.selectedMemberId
    ? members?.find((member) => member.id === billing.selectedMemberId)?.soldToSnapshot
    : null;
  const draftDocument: InvoiceDraftDocumentData = {
    billToSnapshot: billingSnapshot,
    billingSnapshot,
    soldToSnapshot: memberCompanySoldTo
      ? { ...memberCompanySoldTo, customerName: billing.customerName }
      : null,
    customerName: billing.customerName,
    dueDate,
    eventCode,
    eventName,
    eventStartAtUtcMilli,
    eventTimeZone: normalizedEventTimeZone,
    invoiceDate,
    invoiceKind,
    lineItems: lineItems.map((lineItem, index) => ({
      ...lineItem,
      id: lineItem.id || `event-invoice-line-${index + 1}`,
    })),
    manualPaymentMethods,
    publicMemo,
    referencePurchaseOrder,
    subtotalInCents,
    taxInCents,
    taxLabel,
    taxNote,
    taxRegistrationNumber,
    totalInCents,
  };

  return (
    <UnsavedInvoicePreviewModal
      actionLoading={creating}
      chamber={chamber}
      draftDocument={draftDocument}
      onClose={onClose}
      onCreateInvoice={onCreate}
      onSaveDraft={onSaveDraft}
      // Same browser print as Manage Invoices: the preview's shared print CSS
      // sets US Letter with 0.4in margins and zooms the 1070px paper to fit.
      onPrintPdf={() => {
        printInvoicePreviewDocument();
      }}
      open={open}
    />
  );
};

export default EventInvoiceDraftPreview;
